import { IncidentService } from '@/api/services/IncidentService';
import { IncidentSearchCriteria, IncidentSearchDto } from '@/api';
import store from '@/store';
import authModule from '@/store/modules/auth';
import { getModule } from 'vuex-module-decorators';
import callWithContext from '@/api/call-with-context';
import { ToolDefinition } from '../types';

const searchIncidentsTool: ToolDefinition = {
  name: 'search_incidents',
  description: 'Search incidents. Use for counts, lists, details, status, time periods. Returns paginated results (totalElements). Fields: issueNo, name, status (OPEN, IN_PROGRESS, CLOSED), owners (shown as "assignees"), teams, triggerTime, lastOccurredDate, createdDate (all milliseconds). For time queries: use from/to (milliseconds) to filter by createdDate. Display dates only (no time/timezone). Use domain (name) to filter. Use sort for ordering. CRITICAL FOR BREAKDOWNS: never use catalogFilters counts/results. Instead you MUST: (1) Make initial search (no filters) to get catalogFilters.children with all filter values (e.g., OPEN, IN_PROGRESS, CLOSED for status). (2) Make separate filtered searches for ALL values from catalogFilters.children (one call per value: status=["OPEN"], status=["IN_PROGRESS"], status=["CLOSED"]). (3) Get totalElements from each response. (4) Build breakdown using ALL counts. You MUST check every value in catalogFilters.children, not just one. NEVER assume a count is zero without making the filtered search call and checking the totalElements value.',
  input_schema: {
    type: 'object',
    properties: {
      status: {
        type: 'array',
        description: 'Status: OPEN, IN_PROGRESS, CLOSED',
        items: {
          type: 'string',
        },
      },
      textSearch: {
        type: 'string',
        description: 'Match text in name/description',
      },
      itemsPerPage: {
        type: 'integer',
        description: 'Items per page (default: 25)',
      },
      sort: {
        type: 'array',
        description: 'Sort: ["property,ASC"] or ["property,DESC"]. Example: ["createdDate,DESC"]',
        items: {
          type: 'string',
        },
      },
      page: {
        type: 'integer',
        description: 'Page number (default: 0)',
      },
      domain: {
        type: 'string',
        description: 'Domain name (default: selected domain)',
      },
      from: {
        type: 'integer',
        description: 'Filter incidents created >= this date (milliseconds). Use for time queries like "this month" or "last X days".',
      },
      to: {
        type: 'integer',
        description: 'Filter incidents created <= this date (milliseconds). Use for time queries.',
      },
    },
    required: [],
  },
};

/**
 * Execute the search_incidents tool
 */
export async function executeSearchIncidents(
  params: IncidentSearchCriteria,
): Promise<IncidentSearchDto | { error: string }> {
  try {
    // Get the auth module to access selected domain
    const auth = getModule(authModule, store);
    const selectedDomain = auth.selectedDomain?.name;

    // Extract domain from params and use provided domain or fall back to selected domain
    const { domain, ...otherParams } = params;
    const requestBody: IncidentSearchCriteria = {
      ...otherParams,
      domain: domain || selectedDomain,
    };

    const result = await callWithContext('sifflet-ai-chat', () => IncidentService.getAllIncident({
      requestBody,
    }));

    return result;
  } catch (error) {
    return { error: (error as Error).message };
  }
}

export default searchIncidentsTool;
